import React, { Suspense, Fragment } from "react";
import { Route, Routes, Navigate } from "react-router-dom";

// components
import Auth from "components/guard/Auth";
import Guest from "components/guard/Guest";
import Loading from "components/common/Loading";

// pages
import Entrance from "pages/Entrance";
import {
  Signup,
  SignupConfirm,
  SignupPassword,
  SignupInfo,
  SignupDone,
} from "pages/Entrance/signup";
import { Signin, FindPassword, ResetPassword } from "pages/Entrance/signin";
import Home from "pages/Home";
import Notifications from "pages/Notifications";
import Schedule from "pages/Schedule";
import ScheduleInfo from "pages/Schedule/ScheduleInfo";

import Sales from "pages/Sales";
import SalesPeriodDetail from "pages/Sales/period/Detail";
import SalesBranchPeriodDetail from "pages/Sales/period/BranchDetail";

import SalesVoucherDetail from "pages/Sales/voucher/Detail";
import SalesReceivableDetail from "pages/Sales/receivable/Detail";
import SalesPaymentDetail from "pages/Sales/payment/PaymentInfo";
import SalesRefundDetail from "pages/Sales/payment/RefundInfo";

import SalesBranchVoucherDetail from "pages/Sales/voucher/BranchDetail";
import SalesBranchReceivableDetail from "pages/Sales/receivable/BranchDetail";
import Store from "pages/Store";
import All from "pages/All";
import AccountSettings from "pages/All/settings/account";
import EditName from "pages/All/settings/account/EditName";
import PasswordCheck from "pages/All/settings/account/PasswordCheck";
import EditPassword from "pages/All/settings/account/EditPassword";
import RemoveAccount from "pages/All/settings/account/RemoveAccount";
import AppLinkRequests from "pages/All/app-link";
import QNARequests from "pages/All/qna";
import QNADetail from "pages/All/qna/QnaDetail";
import QNAAnswer from "pages/All/qna/QnaAnswer";
import NotificationSettings from "pages/All/settings/notifications";
import MainCenterSettings from "pages/All/settings/mainCenter";
import Terms from "pages/All/settings/terms";
import TermsDetail from "pages/All/settings/terms/TermsDetail";
import CenterSettings from "pages/All/settings/center";
import ImageSettings from "pages/All/settings/center/ImageSettings";
import InfoSettings from "pages/All/settings/center/InfoSettings";
import CEOInfoSettings from "pages/All/settings/center/CEOInfoSettings";

// Business Dashboard Page
import BusinessDashboard from "pages/BusinessDashboard";

const routesConfig = [
  // root
  { exact: true, path: "/", element: () => <Navigate to={"/entrance"} /> },

  // entrance
  { guard: Fragment, path: "/entrance", element: Entrance },
  { guard: Guest, path: "/entrance/signup", element: Signup },
  { guard: Guest, path: "/entrance/signup/confirm", element: SignupConfirm },
  { guard: Guest, path: "/entrance/signup/password", element: SignupPassword },
  { guard: Guest, path: "/entrance/signup/info", element: SignupInfo },
  { guard: Fragment, path: "/entrance/signup/done", element: SignupDone },
  { guard: Guest, path: "/entrance/signin", element: Signin },
  { guard: Guest, path: "/entrance/findpass", element: FindPassword },
  { guard: Guest, path: "/entrance/findpass/reset", element: ResetPassword },

  // home & notifications
  { guard: Auth, path: "/home", element: Home },
  { guard: Auth, path: "/home/notifications", element: Notifications },

  // schedule
  { guard: Auth, path: "/schedule", element: Schedule },
  {
    guard: Auth,
    path: "/schedule/:serviceType/:sequence",
    element: ScheduleInfo,
  },

  // sales
  { guard: Auth, path: "/sales", element: Sales },
  {
    guard: Auth,
    path: "/sales/daily",
    element: () => {
      return <SalesPeriodDetail mode="daily" />;
    },
  },
  {
    guard: Auth,
    path: "/sales/weekly",
    element: () => {
      return <SalesPeriodDetail mode="weekly" />;
    },
  },
  {
    guard: Auth,
    path: "/sales/monthly",
    element: () => {
      return <SalesPeriodDetail mode="monthly" />;
    },
  },
  { guard: Auth, path: "/sales/voucher", element: SalesVoucherDetail },
  { guard: Auth, path: "/sales/receivable", element: SalesReceivableDetail },

  {
    guard: Auth,
    path: "/sales/daily/:seqPartnerBranch",
    element: () => {
      return <SalesPeriodDetail mode="daily" />;
    },
  },
  {
    guard: Auth,
    path: "/sales/weekly/:seqPartnerBranch",
    element: () => {
      return <SalesPeriodDetail mode="weekly" />;
    },
  },
  {
    guard: Auth,
    path: "/sales/monthly/:seqPartnerBranch",
    element: () => {
      return <SalesPeriodDetail mode="monthly" />;
    },
  },
  {
    guard: Auth,
    path: "/sales/voucher/:seqPartnerBranch",
    element: SalesVoucherDetail,
  },
  {
    guard: Auth,
    path: "/sales/receivable/:seqPartnerBranch",
    element: SalesReceivableDetail,
  },

  {
    guard: Auth,
    path: "/sales/order/payment/:seqOrderPayment",
    element: SalesPaymentDetail,
  },
  {
    guard: Auth,
    path: "/sales/refund/payment/:seqRefundInfo",
    element: SalesRefundDetail,
  },

  {
    guard: Auth,
    path: "/sales/branch/daily",
    element: () => {
      return <SalesBranchPeriodDetail mode="daily" />;
    },
  },
  {
    guard: Auth,
    path: "/sales/branch/weekly",
    element: () => {
      return <SalesBranchPeriodDetail mode="weekly" />;
    },
  },
  {
    guard: Auth,
    path: "/sales/branch/monthly",
    element: () => {
      return <SalesBranchPeriodDetail mode="monthly" />;
    },
  },
  {
    guard: Auth,
    path: "/sales/branch/voucher",
    element: SalesBranchVoucherDetail,
  },
  {
    guard: Auth,
    path: "/sales/branch/receivable",
    element: SalesBranchReceivableDetail,
  },

  // schedule
  { guard: Auth, path: "/store", element: Store },

  // report -> 경영 대시보드
  { guard: Auth, path: "/report", element: BusinessDashboard },

  // all -> index
  { guard: Auth, path: "/all", element: All },

  // all -> 관리자 계정 설정
  { guard: Auth, path: "/all/account", element: AccountSettings },
  { guard: Auth, path: "/all/account/name", element: EditName },
  { guard: Auth, path: "/all/account/password-check", element: PasswordCheck },
  { guard: Auth, path: "/all/account/password", element: EditPassword },
  { guard: Auth, path: "/all/account/removal", element: RemoveAccount },

  // all -> 앱 연동 관리 & 문의 사항 관리
  { guard: Auth, path: "/all/app-link", element: AppLinkRequests },
  { guard: Auth, path: "/all/qna", element: QNARequests },
  { guard: Auth, path: "/all/qna/:seqQna", element: QNADetail },
  { guard: Auth, path: "/all/qna/answer/:seqQna", element: QNAAnswer },

  // all -> 내 센터 설정 관리
  {
    guard: Auth,
    path: "/all/settings/notifications",
    element: NotificationSettings,
  },
  {
    guard: Auth,
    path: "/all/settings/maincenter",
    element: MainCenterSettings,
  },
  { guard: Auth, path: "/all/terms", element: Terms },
  { guard: Auth, path: "/all/terms/detail", element: TermsDetail },
  { guard: Auth, path: "/all/settings/center", element: CenterSettings },
  { guard: Auth, path: "/all/settings/center/image", element: ImageSettings },
  { guard: Auth, path: "/all/settings/center/info", element: InfoSettings },
  { guard: Auth, path: "/all/settings/center/ceo", element: CEOInfoSettings },
];

const renderRoutes = (routes) => {
  return (
    routes && (
      <Suspense fallback={<Loading />}>
        <Routes>
          {routes.map((route, i) => {
            const Guard = route.guard || Fragment;
            const Component = route.element;

            return (
              <Route
                key={i}
                path={route.path}
                exact={true}
                element={
                  <Guard>
                    <Component />
                  </Guard>
                }
              />
            );
          })}
        </Routes>
      </Suspense>
    )
  );
};

const AppRoutes = () => {
  return renderRoutes(routesConfig);
};

export default AppRoutes;
